/**
 * The vehicle wiki's data: the roster (every vehicle on every layer, from the map pipeline), the
 * armour manifest and the rounds (both from the SDK armour export). A vehicle is always in the
 * roster; it has armour only once its Blueprint has been exported.
 *
 * Facts the SDK export does not carry (health, tickets, guns and their penetration, 2D protection
 * maps) come from the Squad Wiki, matched per vehicle by tools/src/squad-wiki.mjs.
 *
 * Test rigs from tools/fixtures are in the manifest too. They are not Squad vehicles and appear on
 * the dev server only.
 */
import rosterJson from '@data/squad/vehicles.json';
import armorJson from '@data/squad/armor.json';
import roundsJson from '@data/squad/rounds.json';
import wikiJson from '@data/squad/wiki.json';
import type { Plate, Round } from './pen';

export interface FactionUse { short: string; name: string; layers: number; setups: string[] }
export interface RosterVehicle {
  id: string; name: string; class: string | null; icon: string | null; blueprints: string[]; layers: number;
  respawn_min: { min: number; max: number; usual: number } | null; factions: FactionUse[];
}
export interface Armor { blueprint: string; fixture?: boolean; file: string; plates: Plate[]; rounds: string[] }
export interface WikiStats { class: string | null; ticketCost: number | null; crew: number | null; passengers: number | null; ammoPoints: number | null; constructionPoints: number | null; respawn_min: number | null; maxSpeed_kmh: number | null; revSpeed_kmh: number | null; health: number | null }
export interface WikiImage { file: string; src: string; w: number; h: number; page: string }
export interface WikiVehicle {
  page: string; url: string; revid: number | null;
  /** exact: the wiki variant is this vehicle; close: a near variant; page: linked page only, its stats not used. */
  match: 'exact' | 'close' | 'page'; variant: string | null; stats: WikiStats | null;
  weapons: { slot: string | null; model: string; link: string | null; caliber: string | null; ammo: string | null; rounds: string | null }[];
  guns: string[]; protection: { text: string | null; images: WikiImage[] } | null;
}
export interface WikiRound {
  id: string; weapon: string; name: string; tab: string; damageType: string | null; pen_mm: number | null; curve: [number, number][] | null;
  baseDamage: number | null; minDamage: number | null; splash: number | null; postPen_m: number | null; velocity_ms: number | null; rof: number | null; explosive: boolean;
}
export interface WikiWeapon { page: string; url: string; kind: 'vehicle' | 'launcher' | 'missile'; rounds: WikiRound[] }
type WikiFile = { fetched: string; vehicles: Record<string, WikiVehicle>; weapons: Record<string, WikiWeapon> };
export const wiki = wikiJson as unknown as WikiFile;

export interface Vehicle extends RosterVehicle { armor: Armor | null; fixture: boolean; wiki: WikiVehicle | null }

type ArmorFile = { sdk: string | null; dataAsOf: string; source: string; vehicles: Record<string, Armor> };
const armorFile = armorJson as unknown as ArmorFile;
export const armorMeta = { sdk: armorFile.sdk, dataAsOf: armorFile.dataAsOf, source: armorFile.source };

export const CLASS_NAMES: Record<string, string> = {
  MBT: 'Main battle tank', MGS: 'Mobile gun system', IFV: 'Infantry fighting vehicle', APC: 'Armoured personnel carrier',
  TD: 'Anti-tank', RSV: 'Recon', SPAA: 'Anti-air', SPA: 'Artillery', MRAP: 'MRAP', LTV: 'Light vehicle',
  LOGI: 'Logistics', TRAN: 'Transport', UH: 'Helicopter', ULTV: 'Boat and bike',
};
/** Heaviest first: the order the index lists classes in. */
export const CLASS_ORDER = ['MBT', 'MGS', 'IFV', 'APC', 'TD', 'RSV', 'SPAA', 'SPA', 'MRAP', 'LTV', 'LOGI', 'TRAN', 'UH', 'ULTV'];

export function allVehicles(includeFixtures: boolean): Vehicle[] {
  const roster = (rosterJson as { vehicles: RosterVehicle[] }).vehicles;
  const out: Vehicle[] = roster.map((v) => {
    const a = armorFile.vehicles[v.id];
    return { ...v, armor: a && !a.fixture ? a : null, fixture: false, wiki: wiki.vehicles[v.id] ?? null };
  });
  if (includeFixtures) {
    for (const [id, a] of Object.entries(armorFile.vehicles)) {
      if (!a.fixture) continue;
      const heavy = /heavy/i.test(id);
      out.unshift({
        id, name: `Test rig · ${heavy ? 'heavy' : 'light'}`, class: heavy ? 'MBT' : 'IFV', icon: null, blueprints: [a.blueprint], layers: 0,
        respawn_min: null, factions: [], armor: a, fixture: true, wiki: null,
      });
    }
  }
  return out;
}

/**
 * Rounds the pen check can use. The SDK export's rounds come first when there are any; the Squad
 * Wiki's are always there, with penetration by range where the wiki lists it. A round only counts if
 * it has a penetration worth checking against armour (20 mm and up: no small arms, no HE splinters).
 */
const vehiclesByGun = new Map<string, string[]>();
for (const [id, w] of Object.entries(wiki.vehicles)) for (const g of w.guns) vehiclesByGun.set(g, [...(vehiclesByGun.get(g) ?? []), id]);
export const wikiRounds: (Round & { wiki: WikiRound; weapon: WikiWeapon })[] = Object.values(wiki.weapons).flatMap((w) => w.rounds
  .filter((r) => (r.pen_mm ?? 0) >= 20)
  .map((r) => ({
    id: `wiki-${r.id}`, name: `${w.page} · ${r.tab === w.page ? r.name : r.tab}`, pen_mm: r.pen_mm, curve: r.curve, explosive: r.explosive,
    vehicles: w.kind === 'vehicle' ? vehiclesByGun.get(w.page) ?? [] : [], weapons: [w.page], wiki: r, weapon: w,
  })));
export const rounds: Round[] = [...(roundsJson as unknown as { rounds: Round[] }).rounds, ...wikiRounds];
export const roundById = (id: string) => rounds.find((r) => r.id === id) ?? null;
/** Rounds a vehicle fires (SDK export, then the wiki's), then the infantry launchers and missiles. */
export function roundsFor(v: Vehicle | null): { group: string; rounds: Round[] }[] {
  const own = [
    ...(v?.armor?.rounds ?? []).map(roundById).filter((r): r is Round => !!r),
    ...(v ? wikiRounds.filter((r) => r.vehicles?.includes(v.id)) : []),
  ];
  const sdkInfantry = rounds.filter((r) => !r.id.startsWith('wiki-') && !r.vehicles?.length && (v?.fixture || !r.id.startsWith('fobmap-')));
  const launchers = wikiRounds.filter((r) => r.weapon.kind === 'launcher'), missiles = wikiRounds.filter((r) => r.weapon.kind === 'missile');
  const groups = [];
  if (v && own.length) groups.push({ group: `${v.name} guns`, rounds: own });
  if (sdkInfantry.length) groups.push({ group: 'Infantry anti-tank (SDK)', rounds: sdkInfantry });
  if (launchers.length) groups.push({ group: 'Infantry launchers', rounds: launchers });
  if (missiles.length) groups.push({ group: 'Guided missiles', rounds: missiles });
  return groups;
}

export const factionNames = (vs: Vehicle[]) => [...new Map(vs.flatMap((v) => v.factions.map((f) => [f.short, f.name] as const))).entries()]
  .sort((a, b) => a[1].localeCompare(b[1]));
